<script setup>
/* global axios */
// Katalog CSV ice/disa aktarim sekmesi.
//
// Chatwoot kimlik bilgilerini (api_access_token, hesap basliklari) window.axios
// uzerinde tutar. Ham 'axios' paketi import edilirse istek kimliksiz gider ve
// 401 doner; bu yuzden global axios kullaniliyor.
//
// Neden bu ekran: satici yuzlerce urunu tek tek forma girmek zorunda kalmasin.
// Disa aktarma ayni zamanda "yedek" islevi gorur; kullanici once indirir,
// Excel'de duzenler, geri yukler. Bu dongunun calismasi icin indirilen
// dosyanin sutun duzeni ile beklenen sutun duzeni AYNIDIR.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const emit = defineEmits(['degisti']);

const { t } = useI18n();
const route = useRoute();

const DOSYA_ADI = 'katalog.csv';

// CSV basliklari cevrilmez: bunlar dosyanin icindeki gercek sutun adlaridir,
// arka uc tam bu yazimla okur. Cevrilen sadece aciklamalaridir; aciklama
// anahtarlari SUTUNLAR.ALAN altinda ayri tutuluyor cunku 'aciklama' adinda bir
// sutun var ve bolum basligiyla ayni anahtara denk gelmemeli.
const SUTUNLAR = [
  { ad: 'ad', anahtar: 'AD', zorunlu: true },
  { ad: 'kod', anahtar: 'KOD' },
  { ad: 'aciklama', anahtar: 'ACIKLAMA' },
  { ad: 'fiyat', anahtar: 'FIYAT' },
  { ad: 'para_birimi', anahtar: 'PARA_BIRIMI' },
  { ad: 'stok', anahtar: 'STOK' },
  { ad: 'durum', anahtar: 'DURUM' },
  { ad: 'gorsel_url', anahtar: 'GORSEL_URL' },
  { ad: 'urun_url', anahtar: 'URUN_URL' },
  { ad: 'marka', anahtar: 'MARKA' },
  { ad: 'kategori', anahtar: 'KATEGORI' },
];

const indiriliyor = ref(false);
const yukleniyor = ref(false);
const secilenDosya = ref(null);
const dosyaGirdisi = ref(null);
const sonuc = ref(null);
const hata = ref('');

const ucKok = computed(
  () => `/api/v1/accounts/${route.params.accountId}/katalog`
);

const hataliSatirVar = computed(() => (sonuc.value?.hatalar || []).length > 0);

// responseType: 'blob' kullanildiginda hata govdesi de Blob olarak gelir;
// e.response.data.error dogrudan okunamaz, once metne cevirmek gerekir.
const hataMetni = async e => {
  const veri = e.response?.data;
  if (veri instanceof Blob) {
    try {
      return JSON.parse(await veri.text()).error || e.message;
    } catch {
      // Govde JSON degilse (ornegin HTML hata sayfasi) ham mesaja duseriz.
      return e.message;
    }
  }
  return veri?.error || e.message;
};

const disaAktar = async () => {
  indiriliyor.value = true;
  hata.value = '';
  try {
    const { data } = await axios.get(`${ucKok.value}/disa-aktar`, {
      responseType: 'blob',
    });

    // Tarayicida indirmeyi baslatmanin tek yolu: blob icin gecici bir URL
    // uretip gorunmeyen bir <a download> ogesini tiklamak.
    const adres = window.URL.createObjectURL(data);
    const bag = document.createElement('a');
    bag.href = adres;
    bag.download = DOSYA_ADI;
    document.body.appendChild(bag);
    bag.click();
    document.body.removeChild(bag);
    // Serbest birakilmazsa blob sayfa kapanana kadar bellekte kalir; her
    // indirmede bir kopya birikir.
    window.URL.revokeObjectURL(adres);

    useAlert(t('KATALOG.AKTARIM.DISA.INDIRILDI'));
  } catch (e) {
    hata.value = await hataMetni(e);
  } finally {
    indiriliyor.value = false;
  }
};

const dosyaSecildi = olay => {
  secilenDosya.value = olay.target.files?.[0] || null;
  // Yeni dosya secilince onceki sonuc yaniltici olur, temizlenir.
  sonuc.value = null;
  hata.value = '';
};

const dosyayiOku = dosya =>
  new Promise((coz, red) => {
    const okuyucu = new FileReader();
    okuyucu.onload = () =>
      // Excel'in "CSV UTF-8" bicimi dosyanin basina BOM koyar. Temizlenmezse
      // ilk sutun adi "ad" degil "﻿ad" olur ve arka uc butun satirlari
      // adsiz sanip reddeder.
      coz(String(okuyucu.result || '').replace(/^﻿/, ''));
    okuyucu.onerror = () =>
      red(new Error(t('KATALOG.AKTARIM.ICE.OKUNAMADI')));
    // Dosya METIN olarak okunur; arka uc CSV'yi duz metin bekliyor.
    okuyucu.readAsText(dosya);
  });

const iceAktar = async () => {
  if (!secilenDosya.value) return;

  yukleniyor.value = true;
  hata.value = '';
  sonuc.value = null;
  try {
    const icerik = await dosyayiOku(secilenDosya.value);
    const { data } = await axios.post(`${ucKok.value}/ice-aktar`, { icerik });
    sonuc.value = data;

    // Hatali satir olsa bile eklenenler kaydedildi; liste tazelenmeli.
    emit('degisti');
    useAlert(
      t('KATALOG.AKTARIM.ICE.TAMAMLANDI', {
        eklenen: data.eklenen || 0,
        guncellenen: data.guncellenen || 0,
      })
    );

    // Ayni dosya duzeltilip tekrar secilebilsin diye girdi sifirlanir;
    // aksi halde tarayici "ayni dosya" degisimini olay olarak bildirmez.
    secilenDosya.value = null;
    if (dosyaGirdisi.value) dosyaGirdisi.value.value = '';
  } catch (e) {
    hata.value = e.response?.data?.error || e.message;
  } finally {
    yukleniyor.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 p-4">
    <div>
      <h2 class="text-base font-medium text-n-slate-12">
        {{ t('KATALOG.AKTARIM.BASLIK') }}
      </h2>
      <p class="mt-1 text-sm text-n-slate-11">
        {{ t('KATALOG.AKTARIM.ACIKLAMA') }}
      </p>
    </div>

    <p v-if="hata" class="text-sm text-n-ruby-11">
      {{ hata }}
    </p>

    <div class="grid gap-4 lg:grid-cols-2">
      <!-- Disa aktar -->
      <section class="p-4 border rounded-lg border-n-weak bg-n-surface-1">
        <div class="flex items-center gap-2 mb-1">
          <i class="i-lucide-download text-n-slate-11" />
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('KATALOG.AKTARIM.DISA.BASLIK') }}
          </h3>
        </div>
        <p class="mb-3 text-sm text-n-slate-11">
          {{ t('KATALOG.AKTARIM.DISA.ACIKLAMA') }}
        </p>
        <Button
          size="sm"
          variant="faded"
          icon="i-lucide-download"
          :label="t('KATALOG.AKTARIM.DISA.INDIR')"
          :is-loading="indiriliyor"
          @click="disaAktar"
        />
      </section>

      <!-- Ice aktar -->
      <section class="p-4 border rounded-lg border-n-weak bg-n-surface-1">
        <div class="flex items-center gap-2 mb-1">
          <i class="i-lucide-upload text-n-slate-11" />
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('KATALOG.AKTARIM.ICE.BASLIK') }}
          </h3>
        </div>
        <p class="mb-3 text-sm text-n-slate-11">
          {{ t('KATALOG.AKTARIM.ICE.ACIKLAMA') }}
        </p>

        <div class="flex flex-wrap items-center gap-2">
          <input
            ref="dosyaGirdisi"
            type="file"
            accept=".csv,text/csv"
            class="text-sm max-w-full text-n-slate-11 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-n-weak file:bg-n-alpha-1 file:text-sm file:text-n-slate-12"
            @change="dosyaSecildi"
          />
          <Button
            size="sm"
            variant="solid"
            icon="i-lucide-upload"
            :label="t('KATALOG.AKTARIM.ICE.YUKLE')"
            :disabled="!secilenDosya || yukleniyor"
            :is-loading="yukleniyor"
            @click="iceAktar"
          />
        </div>

        <p v-if="yukleniyor" class="flex items-center gap-2 mt-3 text-sm text-n-slate-11">
          <Spinner class="text-n-brand" />
          {{ t('KATALOG.AKTARIM.ICE.ISLENIYOR') }}
        </p>
      </section>
    </div>

    <!-- Ice aktarim sonucu -->
    <section
      v-if="sonuc"
      class="p-4 border rounded-lg border-n-weak bg-n-surface-1"
    >
      <h3 class="mb-2 text-sm font-medium text-n-slate-12">
        {{ t('KATALOG.AKTARIM.SONUC.BASLIK') }}
      </h3>

      <div class="flex flex-wrap gap-4 mb-3 text-sm">
        <span class="text-n-teal-11">
          {{ t('KATALOG.AKTARIM.SONUC.EKLENEN', { adet: sonuc.eklenen || 0 }) }}
        </span>
        <span class="text-n-teal-11">
          {{
            t('KATALOG.AKTARIM.SONUC.GUNCELLENEN', {
              adet: sonuc.guncellenen || 0,
            })
          }}
        </span>
        <span :class="hataliSatirVar ? 'text-n-ruby-11' : 'text-n-slate-11'">
          {{
            t('KATALOG.AKTARIM.SONUC.HATALI', {
              adet: (sonuc.hatalar || []).length,
            })
          }}
        </span>
      </div>

      <p v-if="!hataliSatirVar" class="text-sm text-n-slate-11">
        {{ t('KATALOG.AKTARIM.SONUC.HATASIZ') }}
      </p>

      <!-- Hatali satirlar tek tek gosterilir: kullanici hangi satiri
           duzeltecegini bilmeden dosyayi tekrar yukleyemez. -->
      <div v-else>
        <p class="mb-2 text-sm text-n-slate-11">
          {{ t('KATALOG.AKTARIM.SONUC.HATA_ACIKLAMA') }}
        </p>
        <div class="overflow-x-auto border rounded-md border-n-weak">
          <table class="w-full text-sm text-left">
            <thead class="bg-n-alpha-1 text-n-slate-11">
              <tr>
                <th class="px-3 py-2 font-medium w-24">
                  {{ t('KATALOG.AKTARIM.SONUC.SUTUN_SATIR') }}
                </th>
                <th class="px-3 py-2 font-medium">
                  {{ t('KATALOG.AKTARIM.SONUC.SUTUN_SEBEP') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(satirHatasi, sira) in sonuc.hatalar"
                :key="`${satirHatasi.satir}-${sira}`"
                class="border-t border-n-weak"
              >
                <td class="px-3 py-2 align-top text-n-slate-12">
                  {{ satirHatasi.satir }}
                </td>
                <td class="px-3 py-2 align-top text-n-ruby-11">
                  {{ satirHatasi.sebep }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <!-- Beklenen sutunlar -->
    <section class="p-4 border rounded-lg border-n-weak bg-n-surface-1">
      <h3 class="mb-1 text-sm font-medium text-n-slate-12">
        {{ t('KATALOG.AKTARIM.SUTUNLAR.BASLIK') }}
      </h3>
      <p class="mb-3 text-sm text-n-slate-11">
        {{ t('KATALOG.AKTARIM.SUTUNLAR.ACIKLAMA') }}
      </p>

      <div class="overflow-x-auto border rounded-md border-n-weak">
        <table class="w-full text-sm text-left">
          <thead class="bg-n-alpha-1 text-n-slate-11">
            <tr>
              <th class="px-3 py-2 font-medium w-40">
                {{ t('KATALOG.AKTARIM.SUTUNLAR.TABLO_SUTUN') }}
              </th>
              <th class="px-3 py-2 font-medium">
                {{ t('KATALOG.AKTARIM.SUTUNLAR.TABLO_ACIKLAMA') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="sutun in SUTUNLAR"
              :key="sutun.ad"
              class="border-t border-n-weak"
            >
              <td class="px-3 py-2 align-top whitespace-nowrap">
                <code class="text-n-slate-12">{{ sutun.ad }}</code>
                <span v-if="sutun.zorunlu" class="ml-1 text-n-ruby-11">
                  {{ t('KATALOG.AKTARIM.SUTUNLAR.ZORUNLU') }}
                </span>
              </td>
              <td class="px-3 py-2 align-top text-n-slate-11">
                {{ t(`KATALOG.AKTARIM.SUTUNLAR.ALAN.${sutun.anahtar}`) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <ul class="mt-3 space-y-1 text-sm list-disc list-inside text-n-slate-11">
        <li>{{ t('KATALOG.AKTARIM.SUTUNLAR.NOT_KOD') }}</li>
        <li>{{ t('KATALOG.AKTARIM.SUTUNLAR.NOT_FIYAT') }}</li>
        <li>{{ t('KATALOG.AKTARIM.SUTUNLAR.NOT_VIRGUL') }}</li>
        <li>{{ t('KATALOG.AKTARIM.SUTUNLAR.NOT_UTF8') }}</li>
      </ul>

      <h4 class="mt-4 mb-2 text-sm font-medium text-n-slate-12">
        {{ t('KATALOG.AKTARIM.SUTUNLAR.ORNEK_BASLIK') }}
      </h4>
      <pre
        class="p-3 overflow-x-auto text-xs border rounded-md border-n-weak bg-n-alpha-1 text-n-slate-12 whitespace-pre"
      >{{ t('KATALOG.AKTARIM.SUTUNLAR.ORNEK_CSV') }}</pre>
    </section>
  </div>
</template>
